import {
  Directive,
  Input,
  HostListener,
  TemplateRef,
  Type,
  ComponentRef,
  ElementRef,
  Renderer2,
  Injector,
  ComponentFactoryResolver,
  ViewContainerRef
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';
import { timer, Subscription } from 'rxjs';
import { Destroyer } from '@apps/tools';

export interface TooltipConfig {
  animate?: boolean;
  clickable?: boolean; // Can mouseenter inside tooltip container
  delay?: number;
}

const defaultConfig: TooltipConfig = {
  animate: true,
  clickable: false,
  delay: 0
};

@Directive({
  // tslint:disable-next-line: directive-selector
  selector: 'sc-tooltip, [sc-tooltip]'
})
export class TooltipDirective extends Destroyer {
  @Input('sc-tooltip') content: string | TemplateRef<any> | Type<any>;

  private _config: TooltipConfig = defaultConfig;
  @Input() set config(config: TooltipConfig) {
    this._config = Object.assign(defaultConfig, config || {});
  }
  get config(): TooltipConfig {
    return this._config;
  }

  private componentRef: ComponentRef<TooltipComponent>;
  private timer$: Subscription = new Subscription();

  @HostListener('mouseenter')
  mouseenter() {
    if (this.componentRef || !this.content) {
      return;
    }
    const factory = this.resolver.resolveComponentFactory(TooltipComponent);

    const inputs = [
      {
        provide: 'tooltipConfig',
        useValue: {
          host: this.element.nativeElement,
          config: this.config
        }
      }
    ];

    const injector = Injector.create({
      providers: inputs
    });

    this.componentRef = this.vcr.createComponent(
      factory,
      0,
      injector,
      this.generateNgContent()
    );
  }

  @HostListener('document:mousemove', ['$event']) mouseleave(
    event: MouseEvent
  ) {
    if (this.componentRef) {
      this.timer$.unsubscribe();
      this.timer$ = timer(this.config.delay || 0).subscribe(() => {
        if (this.config.clickable) {
          const componentRefInstance: any = this.componentRef.instance;
          const tooltipElemRef: ElementRef = componentRefInstance.el;
          if (
            !tooltipElemRef.nativeElement.contains(event.target) &&
            !this.element.nativeElement.contains(event.target)
          ) {
            this.destroy();
          }
        } else if (!this.element.nativeElement.contains(event.target)) {
          this.destroy();
        }
      });
    }
  }

  constructor(
    private element: ElementRef,
    private renderer: Renderer2,
    private injector: Injector,
    private resolver: ComponentFactoryResolver,
    private vcr: ViewContainerRef
  ) {
    super();
  }

  generateNgContent() {
    if (typeof this.content === 'string') {
      const isHtml = ['</', '/>'].find(
        e => (this.content as string).indexOf(e) > -1
      );
      if (isHtml) {
        const div = this.renderer.createElement('div');
        this.renderer.setProperty(div, 'innerHTML', this.content);
        return [[div]];
      } else {
        const element = this.renderer.createText(this.content);
        return [[element]];
      }
    }

    if (this.content instanceof TemplateRef) {
      let context = {};
      try {
        context = (this.content as any)._parentView.context || {};
      } catch (e) {}
      const data = this.vcr.createEmbeddedView(this.content, context);
      return [data.rootNodes];
    }
    // Else it's a component
    const factory = this.resolver.resolveComponentFactory(this.content as any);
    const componentRef = factory.create(this.injector);
    return [[componentRef.location.nativeElement]];
  }

  destroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.componentRef = null;
  }
}
